import PropTypes from 'prop-types'
import { Suspense } from 'react'
import { styled } from '@mui/material'
import { useSelector } from 'react-redux'
import { Navigate } from 'react-router-dom'
import AppLoader from 'components/ui-kit/AppLoader'
import PrivateRoute from './HOC/PrivateRoutes'
import PublicRoutes from './HOC/PublicRoutes'
import { LOADER_TYPE, ROUTE_PATHS } from 'utils/constants'
import { getAuthToken } from 'utils/common-services/cookie.services'
import { ErrorBoundary } from 'react-error-boundary'
import ErrorHandler from 'components/layout/ErrorHandler/index'
import { routerList } from 'routes/routerList' // 👈 add

const SuspenseWrappedComponent = (Component) => {
  return (
    <Suspense
      fallback={
        <StyledLoaderDiv>
          <AppLoader variant={LOADER_TYPE.SCALE} />
        </StyledLoaderDiv>
      }
    >
      <ErrorBoundary FallbackComponent={ErrorHandler}>
        <Component />
      </ErrorBoundary>
    </Suspense>
  )
}

const RouteValidator = ({ hasAuth, component, path }) => {
  const isAuthenticated = getAuthToken()
  const permissions = useSelector(state => state.permissions.permissions)

  // 👇 find first allowed route from routerList
  const getFirstAllowedRoute = () => {
    for (const section of routerList) {
      for (const item of section.sectionChilds) {
        if (!item.hasSideBarOption) continue

        if (item.subMenus?.length) {
          for (const sub of item.subMenus) {
            if (!sub.path) continue
            const normalizedPath = sub.path?.endsWith('/') ? sub.path.slice(0, -1) : sub.path
            if (permissions[normalizedPath] === undefined || permissions[normalizedPath]?.canView === true) {
              return sub.path
            }
          }
        } else {
          if (!item.path) continue
          const normalizedPath = item.path?.endsWith('/') ? item.path.slice(0, -1) : item.path
          if (permissions[normalizedPath] === undefined || permissions[normalizedPath]?.canView === true) {
            return item.path
          }
        }
      }
    }
    return null
  }

  const hasViewPermission = () => {
    if (!hasAuth) return true
    if (!path) return true
    if (!isAuthenticated) return true
    if (!permissions || Object.keys(permissions).length === 0) return true

    const normalizedPath = path?.endsWith('/') ? path.slice(0, -1) : path

    if (permissions[normalizedPath] !== undefined) {
      return permissions[normalizedPath]?.canView !== false
    }

    const matchedPath = Object.keys(permissions).find(permPath =>
      normalizedPath.startsWith(permPath) || permPath.startsWith(normalizedPath)
    )

    if (matchedPath) {
      return permissions[matchedPath]?.canView !== false
    }

    return true
  }

  if (hasAuth && isAuthenticated && Object.keys(permissions).length > 0 && !hasViewPermission()) {
    // 👇 redirect to first allowed route instead of always dashboard
    const firstAllowedRoute = getFirstAllowedRoute()
    if (firstAllowedRoute) {
      return <Navigate to={firstAllowedRoute} replace />
    }
    // no allowed route → access denied
    return <Navigate to={ROUTE_PATHS.accessDenied} replace />
  }

  return hasAuth
    ? (
      <PrivateRoute
        isAuthenticated={isAuthenticated}
        Component={SuspenseWrappedComponent(component)}
      />
      )
    : (
      <PublicRoutes
        isAuthenticated={isAuthenticated}
        Component={SuspenseWrappedComponent(component)}
      />
      )
}

const StyledLoaderDiv = styled('div')({
  position: 'fixed',
  top: '50%',
  left: '50%',
  zIndex: 1
})

RouteValidator.propTypes = {
  component: PropTypes.elementType.isRequired,
  hasAuth: PropTypes.bool,
  path: PropTypes.string
}

export default RouteValidator
